/**
 * @component ry-dropdown
 * @category  overlay
 * @summary   Action-menu dropdown (Fomantic UI `.ui.dropdown` action mode). A trigger button toggles a floating menu of `<ry-menu-item>` actions. Click-outside closes; Escape returns focus to the trigger. For form-field selection, use `<ry-select>` instead — the two are intentionally separate.
 *
 * Reference: Fomantic UI's dropdown.js (action mode). ry-dropdown does NOT
 * model form selection — `<ry-select>` does. ry-dropdown is purely a menu of
 * commands triggered by a button, with auto-positioning above or below.
 *
 * @attribute (none on the host)   Configuration is by composition. The first child `<ry-button>` is the trigger; the child `<ry-menu>` holds the items.
 *
 * @property  state       'open' | 'closed'    Mirrors `data-ry-state`.
 *
 * @method    open()      Open the menu; positions top or bottom based on viewport space; sets aria-expanded; focuses the first item; fires `ry:open`. No-op if already open.
 * @method    close()     Close the menu; clears aria-expanded; clears `data-ry-position`; fires `ry:close`. No-op if already closed.
 * @method    toggle()    Open if closed; close if open.
 *
 * @event ry:beforeopen
 *        detail: {}
 *        fires:  at the top of open() — trigger click or programmatic open() — before aria-expanded, state, portal, or positioning change
 *        cancelable: preventDefault() keeps the menu closed; `ry:open` does not fire
 *
 * @event ry:open
 *        detail: {}
 *        fires:  after open() transitions state to "open"
 *
 * @event ry:beforeclose
 *        detail: {}
 *        fires:  at the top of close() — click outside, Escape, item click, or programmatic close() — before any state change
 *        cancelable: preventDefault() keeps the menu open; `ry:close` does not fire
 *
 * @event ry:close
 *        detail: {}
 *        fires:  after close() transitions state to "closed". Includes the path: user click outside, Escape key, item click, or programmatic close().

 *
 * @keyboard  Escape — closes the menu and returns focus to the trigger. ArrowDown — focus next menu item (wraps). ArrowUp — focus previous menu item (wraps). Enter — activate the focused item (default browser behavior on `<button>` items). Native button keys on the trigger toggle open/close.
 *
 * @state     `data-ry-state` of `"open"` / `"closed"` on the host. `data-ry-position` of `"top"` / `"bottom"` set on open and cleared on close — drives the CSS that anchors the menu.
 *
 * @content
 *   - One `<ry-button>` child (first one): becomes the trigger. Gets `data-ry-target="trigger"`, `aria-haspopup="true"`, `aria-expanded`.
 *   - One `<ry-menu>` child: becomes the panel. Gets `data-ry-target="menu"`, `role="menu"`.
 *   - Inside the menu, `<ry-menu-item>` children become individual actions. Each gets `data-ry-target="item"`, `role="menuitem"`, and a click handler that closes the dropdown.
 *
 * @dom
 *   ry-dropdown[data-ry-state="open|closed"] [data-ry-position?="top|bottom"]
 *     > ry-button.ry-button     [data-ry-target="trigger"]   aria-haspopup="true" aria-expanded="true|false"
 *     > ry-menu.ry-dropdown__menu   [data-ry-target="menu"]  role="menu"
 *       > ry-menu-item.ry-dropdown__item   [data-ry-target="item"]   role="menuitem"
 *       > ry-menu-item   (additional items follow)
 *
 * @gotcha  Items close the dropdown on click — there is no "stay open" mode. If you need a multi-select panel, use `<ry-select multiple>` instead, which is a separate component.
 *
 * @gotcha  Position is decided per-open() — if the page scrolls or the viewport resizes while the menu is open, the position does NOT update. The menu stays where it opened. Close + reopen to recompute.
 *
 * @gotcha  ArrowUp / ArrowDown navigate menu items, but only when the menu is open AND keyboard focus is already inside the menu. From the trigger, the user must first click or Enter to open the menu, then arrows move between items.
 *
 * @gotcha  The element registers `<ry-menu>` and `<ry-menu-item>` as placeholder custom elements globally (no behavior). Don't expect either tag to do anything outside `<ry-dropdown>`.
 *
 * @example basic
 * ```html
 * <ry-dropdown>
 *   <ry-button>Options</ry-button>
 *   <ry-menu>
 *     <ry-menu-item>Edit</ry-menu-item>
 *     <ry-menu-item>Duplicate</ry-menu-item>
 *     <ry-menu-item>Delete</ry-menu-item>
 *   </ry-menu>
 * </ry-dropdown>
 * ```
 *
 * @example with-icons
 * ```html
 * <ry-dropdown>
 *   <ry-button icon><ry-icon name="more-vertical"></ry-icon></ry-button>
 *   <ry-menu>
 *     <ry-menu-item><ry-icon name="edit"></ry-icon> Edit</ry-menu-item>
 *     <ry-menu-item><ry-icon name="trash"></ry-icon> Delete</ry-menu-item>
 *   </ry-menu>
 * </ry-dropdown>
 * ```
 *
 * @example primary-trigger
 * ```html
 * <ry-dropdown>
 *   <ry-button variant="primary">Actions <ry-icon name="chevron-down"></ry-icon></ry-button>
 *   <ry-menu>
 *     <ry-menu-item>Approve</ry-menu-item>
 *     <ry-menu-item>Reject</ry-menu-item>
 *   </ry-menu>
 * </ry-dropdown>
 * ```
 *
 * @tutorial click-toggles
 * ```js
 * const el = mount(`
 *   <ry-dropdown>
 *     <ry-button>Open</ry-button>
 *     <ry-menu>
 *       <ry-menu-item>A</ry-menu-item>
 *     </ry-menu>
 *   </ry-dropdown>
 * `);
 * const trigger = el.querySelector('[data-ry-target="trigger"]');
 * await expectEqual(el.state, 'closed', 'starts closed');
 * await expectEvent(el, 'ry:open', () => trigger.click());
 * await expectEqual(el.state, 'open', 'click opens');
 * await expectEvent(el, 'ry:close', () => trigger.click());
 * await expectEqual(el.state, 'closed', 'second click closes');
 * ```
 *
 * @tutorial item-click-closes
 * ```js
 * // Wrong assumption: items stay-open like a multi-select. They do NOT —
 * // each item click closes the dropdown after firing its own click event.
 * const el = mount(`
 *   <ry-dropdown>
 *     <ry-button>Open</ry-button>
 *     <ry-menu>
 *       <ry-menu-item id="x">A</ry-menu-item>
 *     </ry-menu>
 *   </ry-dropdown>
 * `);
 * el.open();
 * await expectEqual(el.state, 'open');
 * el.querySelector('#x').click();
 * await expectEqual(el.state, 'closed', 'item click closes the dropdown');
 * ```
 *
 * @tutorial escape-closes-and-returns-focus
 * ```js
 * // Wrong assumption: Escape closes but leaves focus wherever it was.
 * // It also returns focus to the trigger. Critical for keyboard users.
 * const el = mount(`
 *   <ry-dropdown>
 *     <ry-button>Open</ry-button>
 *     <ry-menu>
 *       <ry-menu-item>A</ry-menu-item>
 *     </ry-menu>
 *   </ry-dropdown>
 * `);
 * const trigger = el.querySelector('[data-ry-target="trigger"]');
 * el.open();
 * document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
 * await expectEqual(el.state, 'closed', 'escape closes');
 * await expectEqual(document.activeElement, trigger, 'and returns focus to the trigger');
 * ```
 *
 * @tutorial click-outside-closes
 * ```js
 * const el = mount(`
 *   <div>
 *     <ry-dropdown>
 *       <ry-button>Open</ry-button>
 *       <ry-menu><ry-menu-item>A</ry-menu-item></ry-menu>
 *     </ry-dropdown>
 *     <span id="outside">elsewhere</span>
 *   </div>
 * `);
 * const dd = el.querySelector('ry-dropdown');
 * dd.open();
 * await expectEqual(dd.state, 'open');
 * el.querySelector('#outside').click();
 * await expectEqual(dd.state, 'closed', 'clicking outside closes the dropdown');
 * ```
 *
 * @tutorial aria-haspopup-and-expanded
 * ```js
 * const el = mount(`
 *   <ry-dropdown>
 *     <ry-button>Open</ry-button>
 *     <ry-menu><ry-menu-item>A</ry-menu-item></ry-menu>
 *   </ry-dropdown>
 * `);
 * const trigger = el.querySelector('[data-ry-target="trigger"]');
 * await expectAttr(trigger, 'aria-haspopup', 'true');
 * await expectAttr(trigger, 'aria-expanded', 'false');
 * el.open();
 * await expectAttr(trigger, 'aria-expanded', 'true');
 * el.close();
 * await expectAttr(trigger, 'aria-expanded', 'false');
 * ```
 *
 * @tutorial open-is-idempotent
 * ```js
 * // open() called repeatedly should NOT fire ry:open multiple times.
 * const el = mount(`
 *   <ry-dropdown>
 *     <ry-button>X</ry-button>
 *     <ry-menu><ry-menu-item>A</ry-menu-item></ry-menu>
 *   </ry-dropdown>
 * `);
 * const events = [];
 * el.addEventListener('ry:open', () => events.push(1));
 * el.open(); el.open(); el.open();
 * await expectEqual(events.length, 1, 'open() is idempotent');
 * ```
 *
 * @tutorial beforeopen-veto
 * ```js
 * // preventDefault() on ry:beforeopen keeps the menu closed (trigger click
 * // or el.open()); ry:open stays silent. Without the veto it opens.
 * const el = mount(`
 *   <ry-dropdown>
 *     <ry-button>X</ry-button>
 *     <ry-menu><ry-menu-item>A</ry-menu-item></ry-menu>
 *   </ry-dropdown>
 * `);
 * const opened = [];
 * el.addEventListener('ry:open', () => opened.push(1));
 * el.addEventListener('ry:beforeopen', (e) => e.preventDefault(), { once: true });
 * el.querySelector('[data-ry-target="trigger"]').click();
 * await expectEqual(el.state, 'closed', 'vetoed open leaves state closed');
 * await expectEqual(opened.length, 0, 'ry:open did not fire');
 * el.open();
 * await expectEqual(el.state, 'open', 'without the veto, open() works');
 * ```
 *
 * @tutorial beforeclose-veto
 * ```js
 * // preventDefault() on ry:beforeclose keeps the menu open — Escape, click
 * // outside, item click, or el.close(). ry:close stays silent.
 * const el = mount(`
 *   <ry-dropdown>
 *     <ry-button>X</ry-button>
 *     <ry-menu><ry-menu-item>A</ry-menu-item></ry-menu>
 *   </ry-dropdown>
 * `);
 * el.open();
 * const closed = [];
 * el.addEventListener('ry:close', () => closed.push(1));
 * el.addEventListener('ry:beforeclose', (e) => e.preventDefault(), { once: true });
 * el.close();
 * await expectEqual(el.state, 'open', 'vetoed close leaves state open');
 * await expectEqual(closed.length, 0, 'ry:close did not fire');
 * el.close();
 * await expectEqual(el.state, 'closed', 'without the veto, close() works');
 * ```
 *
 * @see ry-button
 * @see ry-select
 * @see ry-tooltip
 */


import { RyElement } from '../core/ry-element.js';
import { attachFloating } from '../core/ry-floating.js';

export class RyDropdown extends RyElement {
  #menu: HTMLElement | null = null;
  #floatingCleanup: (() => void) | null = null;
  #portalRestore: (() => void) | null = null;

  setup(): void {
    // Handle Option B: transform minimal markup
    this.#transformMinimalMarkup();

    const trigger = this.$<HTMLElement>('[data-ry-target="trigger"]');
    this.#menu = this.$<HTMLElement>('[data-ry-target="menu"], ry-menu');

    if (trigger) {
      this.on(trigger, 'click', () => this.toggle());
      trigger.setAttribute('aria-haspopup', 'true');
      trigger.setAttribute('aria-expanded', 'false');
    }

    // Outside-click closes. The trigger AND the (potentially portalled) menu
    // are treated as "inside" — clicking either should not register as
    // outside. Direct listener because the portalled menu is no longer a
    // descendant of `this` after open, so the shared helper's
    // `this.contains(target)` check would miss it.
    this.on(document, 'click', (e: Event) => {
      if (this.state !== 'open') return;
      const t = e.target as Node | null;
      if (!t) return;
      if (this.contains(t)) return;
      if (this.#menu && this.#menu.contains(t)) return;
      this.close();
    });

    // Escape + arrow key navigation
    this.on(document, 'keydown', this.#handleKeydown);

    // Menu item clicks close
    this.$$('[data-ry-target="item"], ry-menu-item').forEach(item => {
      this.on(item, 'click', () => this.close());
    });

    // Initialize menu state mirror
    if (this.#menu) this.#menu.setAttribute('data-ry-state', 'closed');

    // Initialize state
    this.state = 'closed';
  }

  #transformMinimalMarkup(): void {
    // First ry-button child is the trigger
    // data-ry-target for JS queries, class for CSS styling
    const triggerButton = this.$('ry-button:first-of-type');
    if (triggerButton && !this.$('[data-ry-target="trigger"]')) {
      triggerButton.setAttribute('data-ry-target', 'trigger');
    }

    // Handle ry-menu
    const menu = this.$('ry-menu');
    if (menu && !menu.hasAttribute('data-ry-target')) {
      menu.setAttribute('data-ry-target', 'menu');
      menu.classList.add('ry-dropdown__menu');
      menu.setAttribute('role', 'menu');
    }

    // Handle ry-menu-item
    this.$$('ry-menu-item').forEach(item => {
      if (!item.hasAttribute('data-ry-target')) {
        item.setAttribute('data-ry-target', 'item');
        item.classList.add('ry-dropdown__item');
        item.setAttribute('role', 'menuitem');
      }
    });
  }

  #handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.state === 'open') {
      this.close();
      this.$<HTMLElement>('[data-ry-target="trigger"]')?.focus();
    }

    // Arrow key navigation within menu
    if (this.state === 'open' && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault();
      const items = this.$$<HTMLElement>('[data-ry-target="item"], ry-menu-item');
      const currentIndex = items.indexOf(document.activeElement as HTMLElement);

      let newIndex: number;
      if (event.key === 'ArrowDown') {
        newIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
      } else {
        newIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
      }

      items[newIndex]?.focus();
    }
  };

  open(): void {
    if (this.state === 'open') return;
    if (!this.emit('beforeopen')) return;

    const trigger = this.$<HTMLElement>('[data-ry-target="trigger"]');
    trigger?.setAttribute('aria-expanded', 'true');


    this.state = 'open';

    // Portal the menu to <body> so it escapes any ancestor stacking context.
    // This is the proper fix for the dropdown-clipped-behind-siblings issue
    // (previously worked around by a :has() z-index hack in kitchen-sink CSS).
    if (this.#menu) {
      this.#portalRestore = this.portalToBody(this.#menu);
      this.#menu.setAttribute('data-ry-state', 'open');

      // Anchor with Floating UI. autoUpdate keeps the position correct on
      // scroll/resize — a real upgrade over the previous "compute once per
      // open and stay stale" behavior.
      if (trigger) {
        this.#floatingCleanup = attachFloating(trigger, this.#menu, {
          placement: 'bottom-start',
          offset: 4,
          flip: true,
          shift: true,
          reflectPlacementAttribute: true,
        });
      }
    }

    // Focus first item
    this.nextFrame().then(() => {
      const firstItem = this.#menu?.querySelector<HTMLElement>('[data-ry-target="item"], ry-menu-item')
        ?? this.$<HTMLElement>('[data-ry-target="item"], ry-menu-item');
      firstItem?.focus();
    });

    this.emit('open');
  }

  close(): void {
    if (this.state === 'closed') return;
    if (!this.emit('beforeclose')) return;

    const trigger = this.$('[data-ry-target="trigger"]');
    trigger?.setAttribute('aria-expanded', 'false');


    this.state = 'closed';

    // Detach FUI + restore menu to its authored DOM position
    this.#floatingCleanup?.();
    this.#floatingCleanup = null;
    if (this.#menu) {
      this.#menu.setAttribute('data-ry-state', 'closed');
      this.#menu.removeAttribute('data-ry-position');
    }
    this.#portalRestore?.();
    this.#portalRestore = null;

    this.removeAttribute('data-ry-position');
    this.emit('close');
  }

  toggle(): void {
    if (this.state === 'open') {
      this.close();
    } else {
      this.open();
    }
  }

  teardown(): void {
    this.#floatingCleanup?.();
    this.#portalRestore?.();
  }
}

customElements.define('ry-dropdown', RyDropdown);

// Register placeholders for minimal markup
if (!customElements.get('ry-menu')) {
  customElements.define('ry-menu', class extends HTMLElement {});
}
if (!customElements.get('ry-menu-item')) {
  customElements.define('ry-menu-item', class extends HTMLElement {});
}
